import axios from 'axios';

/**
 * The single HTTP door to the API.
 *
 * Two things make this file worth reading:
 *
 * 1. **The access token lives in memory, never in `localStorage`.** The refresh token is
 *    an `httpOnly` cookie the JS cannot touch (docs/03 §6), so a stolen XSS payload gets
 *    at most the 15 minutes left on the current access token, and only while the tab is
 *    open. The cost is that a page reload starts with no token — which is what the silent
 *    refresh on boot is for.
 * 2. **A 401 refreshes once, and concurrent 401s share that one refresh.** Without the
 *    shared promise, a screen firing six queries at once would fire six refreshes; the
 *    server rotates the token on every use and treats reuse of an old one as theft, so
 *    the parallel refreshes would revoke the whole family and log the user out.
 */

export const API_BASE = '/api/v1';

/** Normalised error the whole app throws — `{ error: { code, message, details, requestId } }`. */
export class ApiError extends Error {
  constructor({ code, message, details, requestId, status }) {
    super(message || 'Request failed');
    this.name = 'ApiError';
    this.code = code || 'UNKNOWN';
    this.details = details || null;
    this.requestId = requestId || null;
    this.status = status ?? 0;
  }

  /** 403 from the permission guard, as opposed to a scope 404. */
  get isForbidden() {
    return this.status === 403;
  }

  get isUnauthenticated() {
    return this.status === 401;
  }

  /** Field errors keyed by path, ready to hand to react-hook-form's setError. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries(this.details.filter((d) => d?.path).map((d) => [d.path, d.message]));
  }

  /**
   * The same errors as one readable line, for a form that has no field to point at.
   *
   * `VALIDATION_ERROR` says only "The submitted data is invalid", which is true and useless:
   * a dialog that posts a value it never showed the operator — a section id, a derived
   * default — leaves them with a refusal and nothing to change. The paths are what make it
   * actionable, and they are already in the response.
   */
  get fieldSummary() {
    if (!Array.isArray(this.details)) return null;
    const lines = this.details
      .filter((d) => d?.message)
      .map((d) => (d.path ? `${d.path}: ${d.message}` : d.message));
    return lines.length ? lines.join(' · ') : null;
  }
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // the refresh cookie
  headers: { 'Content-Type': 'application/json' },
});

// --- session state (module-local by design; see the note above) --------------

let accessToken = null;
let activeSectionId = null;
/** Called when the session cannot be recovered — wired to the auth store at boot. */
let onSessionLost = () => {};

export const setAccessToken = (token) => {
  accessToken = token || null;
};
export const getAccessToken = () => accessToken;

/** Sent as `X-Section-Id`; the server treats it as a narrowing hint only (docs/03 §5). */
export const setActiveSectionHeader = (sectionId) => {
  activeSectionId = sectionId || null;
};

export const setSessionLostHandler = (fn) => {
  onSessionLost = typeof fn === 'function' ? fn : () => {};
};

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  if (activeSectionId) config.headers['X-Section-Id'] = activeSectionId;
  return config;
});

// --- refresh ----------------------------------------------------------------

/** Endpoints that must never trigger a refresh retry — they *are* the session. */
const NO_RETRY = ['/auth/login', '/auth/refresh', '/auth/logout'];

let refreshInFlight = null;

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers await the
 * same promise, so the rotation happens exactly once.
 */
export const refreshSession = () => {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post('/auth/refresh')
      .then((res) => {
        const { accessToken: token, user } = res.data.data;
        setAccessToken(token);
        return { accessToken: token, user };
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const toApiError = (error) => {
  if (error.response) {
    const body = error.response.data?.error || {};
    return new ApiError({ ...body, status: error.response.status });
  }
  if (error.code === 'ERR_CANCELED') {
    return new ApiError({ code: 'CANCELLED', message: 'Request cancelled', status: 0 });
  }
  return new ApiError({
    code: 'NETWORK_ERROR',
    message: 'Cannot reach the server. Check your connection and try again.',
    status: 0,
  });
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isAuthRoute = NO_RETRY.some((path) => (original.url || '').startsWith(path));

    if (status === 401 && !original._retried && !isAuthRoute) {
      original._retried = true;
      try {
        await refreshSession();
        return api(original);
      } catch {
        setAccessToken(null);
        onSessionLost();
        throw toApiError(error);
      }
    }

    throw toApiError(error);
  },
);

// --- thin verb helpers ------------------------------------------------------
// Every feature's `*.api.js` goes through these, so the `{ data, meta }` envelope is
// unwrapped in exactly one place.

export const get = async (url, config) => (await api.get(url, config)).data;
export const post = async (url, body, config) => (await api.post(url, body, config)).data;
export const patch = async (url, body, config) => (await api.patch(url, body, config)).data;
export const del = async (url, config) => (await api.delete(url, config)).data;

/** Unwrap to `data` for the common single-resource case. */
export const getData = async (url, config) => (await get(url, config)).data;

export default api;
